<script context="module" lang="ts">
  import type { VoterMapPoint, VoterSummary } from '../lib/stores/voters';
  import { apiFetchAuthed } from '../lib/api';
  import { daysSince, householdColor, householdOutreachStateOf } from '../lib/mapColors';

  // The marker icon and cluster colour only need the contact-state fields, the
  // DNC flag (which also affects the rendered pin), the donor/volunteer flags
  // pinColor reads for its supporter-action tier, and the outreach state.
  export type IconInput = Pick<
    VoterMapPoint,
    | 'latest_contact_result'
    | 'latest_support_level'
    | 'do_not_contact'
    | 'latest_contact_at'
    | 'text_outreach'
    | 'has_donated'
    | 'has_volunteered'
    | 'has_voted'
  >;

  /**
   * Rebuild a single-voter marker's cached icon input after a contact.
   *
   * text_outreach is carried from the previous value because a contact never
   * changes it (rebuilding _voterData without it would repaint the pin as "no
   * text data" in outreach mode). has_donated and has_volunteered come from
   * the updated voter, because recording a donated or volunteered contact
   * sets them and deleting contacts can clear them; the previous value is
   * only a fallback for an updated object that lacks the key. Every
   * refreshPin caller passes a VoterPublic from GET /api/voters/{id}, or a
   * copy of one patched after a contact or a voter edit, so both are always
   * present. Dropping either flag here silvers a donor or
   * volunteer pin back to whatever its contact-result tier would otherwise
   * paint (see pinColor in lib/mapColors.ts). Lives in the module script, and is
   * exported, so the carry-through can be asserted directly; refreshPin
   * itself needs a live Leaflet marker, which jsdom has no way to give it.
   */
  export function refreshedPinData(
    prev: IconInput | undefined,
    updated: VoterSummary,
  ): IconInput {
    return {
      text_outreach: prev?.text_outreach ?? null,
      latest_contact_result: updated.latest_contact_result,
      latest_support_level: updated.latest_support_level,
      do_not_contact: updated.do_not_contact,
      latest_contact_at: updated.latest_contact_at,
      has_donated: updated.has_donated ?? prev?.has_donated,
      has_volunteered: updated.has_volunteered ?? prev?.has_volunteered,
      // A contact never changes ballot state; the next map fetch does.
      has_voted: updated.has_voted ?? prev?.has_voted,
    };
  }

  /**
   * Rebuild a stored map point after a contact, with the same field rules as
   * a single-voter pin (refreshedPinData), over the point's own identity and
   * location fields, plus an edited age, which the voter picker row shows.
   * refreshPin applies it to the matching entry of mapPointsStore and of
   * currentPoints (the set view mode flips, the same-address picker and the
   * heat layer rebuild from), and to a household marker's member, which the marker's click handler
   * dispatches to the voter picker (its row dot reads the donor and volunteer
   * flags through pinColor).
   */
  export function refreshedMapPoint(
    prev: VoterMapPoint,
    updated: VoterSummary,
  ): VoterMapPoint {
    const next = { ...prev, ...refreshedPinData(prev, updated) };
    if ('age' in updated) next.age = updated.age;
    return next;
  }

  /**
   * Project a full VoterSummary (the shape /api/voters returns, used on
   * filter re-fetch) down to the lean VoterMapPoint fields the map renders
   * with. The backend always sends the text_outreach key (schemas_voter.py's
   * VoterPublic defaults it to null, never omits it); the ?? null here only
   * normalises a missing key to null for type consistency. Pure and exported
   * so the projection, including this task's text_outreach carry-through,
   * has a test that never touches Leaflet; refreshMarkers itself needs a
   * live clusterGroup, which jsdom has no way to give it.
   */
  export function mapPointFromVoter(v: VoterSummary): VoterMapPoint {
    return {
      id: v.id,
      lat: v.lat,
      lng: v.lng,
      do_not_contact: v.do_not_contact,
      latest_support_level: v.latest_support_level,
      latest_contact_result: v.latest_contact_result,
      latest_contact_at: v.latest_contact_at,
      text_outreach: v.text_outreach ?? null,
      has_donated: v.has_donated,
      has_volunteered: v.has_volunteered,
      has_voted: v.has_voted,
      county: v.county,
      party: v.party,
      age: v.age,
      first_name: v.first_name,
      last_name: v.last_name,
      latest_contact_by_user_id: null,
      newly_registered: v.newly_registered,
      petition_signed: v.petition_signed,
    };
  }

  type ViewMode = 'support' | 'coverage' | 'uncontacted' | 'outreach';
  /** The three icon sets Map.svelte can paint a marker with. */
  type IconModeName = 'support' | 'coverage' | 'outreach';

  /** Which icon set a mode paints with; 'uncontacted' reuses the support set. */
  export function iconMode(mode: ViewMode): IconModeName {
    if (mode === 'coverage') return 'coverage';
    if (mode === 'outreach') return 'outreach';
    return 'support';
  }

  /**
   * Icon cache key for a single-voter marker. Pure and exported so the
   * spec's one performance-critical rule is directly testable without
   * Leaflet: the outreach branch keys per STATE (six possible values), never
   * per voter id, which is what keeps the first paint fast across 24k pins.
   */
  export function iconKey(v: IconInput, mode: IconModeName, nowMs: number): string {
    const key = _iconKeyBase(v, mode, nowMs);
    // Only voted pins get a suffix, so every existing key is unchanged.
    return v.has_voted ? `${key}|voted` : key;
  }

  function _iconKeyBase(v: IconInput, mode: IconModeName, nowMs: number): string {
    if (mode === 'coverage') {
      // Buckets must mirror coverageColor's thresholds (7/30/60/120) so two
      // voters that produce different colors never share a cache entry.
      let bucket: string;
      if (v.do_not_contact) bucket = 'dnc';
      else {
        const d = daysSince(v.latest_contact_at, nowMs);
        if (d < 7) bucket = '0';
        else if (d < 30) bucket = '1';
        else if (d < 60) bucket = '2';
        else if (d < 120) bucket = '3';
        else bucket = '4';
      }
      return `cov|${bucket}`;
    }
    if (mode === 'outreach') {
      // One entry per state (six in total), never per voter id: the icon cache
      // is what keeps the 24k-pin first paint fast.
      return `out|${v.do_not_contact ? 'dnc' : (v.text_outreach ?? 'none')}`;
    }
    return `sup|${v.do_not_contact ? 'dnc' : 'live'}|${v.latest_contact_result ?? '-'}|${v.latest_support_level ?? '-'}|${v.has_donated ? 'd' : '-'}|${v.has_volunteered ? 'v' : '-'}`;
  }

  /** Icon cache key for a household marker; see iconKey's outreach note. */
  export function householdIconKey(voters: VoterMapPoint[], mode: IconModeName, nowMs: number): string {
    const n = voters.length;
    if (voters.every(v => v.do_not_contact)) return `house|${mode}|${n}|dnc`;
    if (mode === 'coverage') {
      let latestMs = -Infinity;
      for (const v of voters) {
        if (v.do_not_contact || !v.latest_contact_at) continue;
        const t = Date.parse(v.latest_contact_at);
        if (Number.isFinite(t)) latestMs = Math.max(latestMs, t);
      }
      const d = latestMs === -Infinity ? Infinity : (nowMs - latestMs) / 86400000;
      let bucket: string;
      if (d < 7) bucket = '0';
      else if (d < 30) bucket = '1';
      else if (d < 60) bucket = '2';
      else if (d < 120) bucket = '3';
      else bucket = '4';
      return `house|cov|${n}|${bucket}`;
    }
    if (mode === 'outreach') {
      // Same aggregation the SVG uses, so key and picture cannot disagree.
      return `house|out|${n}|${householdOutreachStateOf(voters)}`;
    }
    // Bucket by the same boundaries clusterColor uses so the cached key
    // collapses households that render identically.
    return `house|sup|${n}|${householdColor(voters)}`;
  }
</script>

<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { get } from 'svelte/store';
  import L from 'leaflet';
  import 'leaflet/dist/leaflet.css';
  import 'leaflet.markercluster/dist/MarkerCluster.css';
  import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
  import 'leaflet.markercluster';
  import 'leaflet.heat';
  import {
    mapPointsStore,
    fetchMapPoints,
    loadCachedMapPoints,
  } from '../lib/stores/voters';
  import {
    pinSvg, pinSvgCoverage, pinSvgOutreach, clusterIconColor, mutedPinSvg,
    householdSvg, householdSvgCoverage, householdSvgOutreach,
  } from '../lib/mapColors';
  import { toast } from '$lib/toast';
  import { polygonStore, clearPolygon } from '../lib/stores/polygon';
  import { pointInPolygon } from '../lib/polygonGeom';
  import { heatPoints } from '../lib/uncontactedHeat';
  import PolygonDrawController from './PolygonDrawController.svelte';

  export let viewMode: ViewMode = 'support';
  /** 'idle' = no polygon and not drawing; 'drawing' = capturing vertices; 'active' = polygon committed and rendered. */
  export let polygonMode: 'idle' | 'drawing' | 'active' = 'idle';

  const dispatch = createEventDispatcher<{
    selectVoter: VoterSummary;
    selectGroup: VoterMapPoint[];
    walkThisArea: void;
    cancel: void;
    // The first load (cache paint, then fresh fetch) is drawn; the route
    // waits for this before applying filters or an audience switch.
    ready: void;
  }>();

  let polygonLayer: L.Polygon | null = null;
  let polygonCount = 0;

  let mapEl: HTMLDivElement;
  let map: L.Map;
  let clusterGroup: L.MarkerClusterGroup;
  // voter id → marker, for optimistic pin-color updates.
  // For households (2+ voters at the same lat/lng), every voter id in the
  // household maps to the same shared marker.
  const markerMap = new Map<string, L.Marker>();
  // The voters the map is currently drawing: the store on a plain load, or
  // the route's filtered set after refreshMarkers. View mode flips, the heat
  // layer and the same-address picker all work from this set, so an active
  // filter survives them.
  let currentPoints: VoterMapPoint[] = [];

  type MarkerAux = L.Marker & {
    _voterData?: IconInput;
    _householdVoters?: VoterMapPoint[];
  };
  // leaflet.heat layer for the uncontacted-density view (added on top of the
  // normal support markers, which stay tappable).
  let heatLayer: any = null;
  let locationDot: L.CircleMarker | null = null;

  let locationWatchId: number | null = null;
  let locationDenied = false;
  // Toggle state for the My Location button. ON = dot shown + position watched.
  let locationActive = false;
  let hasGeolocation = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  // Tracks whether this component has been torn down — set in onDestroy so
  // any in-flight fetch in onMount knows to bail before mutating dead state.
  let destroyed = false;
  // In-flight marker click; lets us ignore the late winner if user clicks again.
  let lastClickAt = 0;

  // Pre-built icon cache. With ~8 distinct visual states across 24k voters,
  // building a fresh L.divIcon per marker (each with a stringified SVG) was a
  // big chunk of the slow first paint. Key by (DNC, result, support) and reuse.
  const iconCache = new Map<string, L.DivIcon>();
  function buildIcon(v: IconInput, mode: IconModeName = iconMode(viewMode), nowMs: number = Date.now()): L.DivIcon {
    const key = iconKey(v, mode, nowMs);
    let icon = iconCache.get(key);
    if (icon) return icon;
    let svg = mode === 'coverage'
      ? pinSvgCoverage(v as VoterMapPoint, nowMs)
      : mode === 'outreach'
        ? pinSvgOutreach(v as VoterMapPoint)
        : pinSvg(v as VoterSummary);
    if (v.has_voted) svg = mutedPinSvg(svg);
    let size: number;
    if (v.do_not_contact) {
      size = 17;
    } else if (mode === 'coverage' || mode === 'outreach') {
      size = 16; // uniform size; color carries the signal in both modes
    } else if (v.latest_contact_result === 'spoke') {
      size = 18;
    } else if (v.latest_contact_result) {
      size = 16;
    } else {
      size = 14;
    }
    icon = L.divIcon({
      html: svg,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    iconCache.set(key, icon);
    return icon;
  }

  function buildHouseholdIcon(voters: VoterMapPoint[], mode: IconModeName = iconMode(viewMode), nowMs: number = Date.now()): L.DivIcon {
    const key = householdIconKey(voters, mode, nowMs);
    let icon = iconCache.get(key);
    if (icon) return icon;
    const svg = mode === 'coverage'
      ? householdSvgCoverage(voters, nowMs)
      : mode === 'outreach'
        ? householdSvgOutreach(voters)
        : householdSvg(voters);
    const size = voters.every(v => v.do_not_contact) ? 20 : 22;
    icon = L.divIcon({
      html: svg,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    iconCache.set(key, icon);
    return icon;
  }

  // Re-render all markers when the view mode flips. Clear the icon cache so
  // new keys are computed against the new mode. Also (re)build or remove the
  // uncontacted heat layer.
  let lastViewMode: ViewMode = viewMode;
  $: if (viewMode !== lastViewMode && clusterGroup) {
    lastViewMode = viewMode;
    iconCache.clear();
    addVoterMarkers(currentPoints, /* doFitBounds */ false);
    updateHeatLayer();
  }

  /**
   * Add the heat layer in 'uncontacted' mode, refresh it if already present,
   * or remove it when leaving the mode. Safe to call repeatedly.
   */
  function updateHeatLayer() {
    if (!map) return;
    if (viewMode === 'uncontacted') {
      const pts = heatPoints(currentPoints);
      if (!heatLayer) {
        heatLayer = (L as any).heatLayer(pts, {
          radius: 28,
          blur: 18,
          maxZoom: 15,
          gradient: { 0.3: '#74c476', 0.6: '#f59e0b', 1: '#a50026' },
        }).addTo(map);
      } else {
        heatLayer.setLatLngs(pts);
      }
    } else if (heatLayer) {
      heatLayer.remove();
      heatLayer = null;
    }
  }

  // React to polygonStore changes: render or remove the polygon layer
  // and recompute which markers are dimmed.
  $: if (clusterGroup) {
    const poly = $polygonStore;
    // Remove the previous polygon layer
    if (polygonLayer) {
      map.removeLayer(polygonLayer);
      polygonLayer = null;
    }
    if (poly && poly.length >= 3) {
      polygonLayer = L.polygon(poly.map(v => [v.lat, v.lng]), {
        color: 'var(--leaf-bright, #4a8a3e)',
        weight: 2,
        fillColor: 'var(--sun-soft, #f5e6b0)',
        fillOpacity: 0.25,
        interactive: false,
      });
      map.addLayer(polygonLayer);
    }
    // Compute count
    const pts = get(mapPointsStore);
    if (poly && poly.length >= 3) {
      let n = 0;
      for (const p of pts) {
        if (p.lat != null && p.lng != null && pointInPolygon(p.lat, p.lng, poly)) {
          n++;
        }
      }
      polygonCount = n;
    } else {
      polygonCount = 0;
    }
    applyDimToMarkers();
  }

  function applyDimToMarkers() {
    const poly = $polygonStore;
    const pts = get(mapPointsStore);
    const byId = new Map<string, VoterMapPoint>();
    for (const p of pts) byId.set(p.id, p);
    markerMap.forEach((m, id) => {
      const v = byId.get(id);
      const el = (m as any).getElement?.() as HTMLElement | null;
      if (!el) return;
      if (poly && poly.length >= 3 && v && v.lat != null && v.lng != null) {
        if (pointInPolygon(v.lat, v.lng, poly)) el.classList.remove('dimmed');
        else el.classList.add('dimmed');
      } else {
        el.classList.remove('dimmed');
      }
    });
  }

  function buildClusterIcon(cluster: L.MarkerCluster) {
    const children = cluster.getAllChildMarkers() as MarkerAux[];
    // Flatten household markers so the cluster averages across the real
    // underlying voters, not the deduped household pins. Same reason the
    // displayed count uses voter total, not marker count.
    const voters: VoterSummary[] = [];
    let total = 0;
    for (const m of children) {
      if (m._householdVoters) {
        for (const v of m._householdVoters) voters.push(v as unknown as VoterSummary);
        total += m._householdVoters.length;
      } else if (m._voterData) {
        voters.push(m._voterData as VoterSummary);
        total += 1;
      } else {
        total += 1;
      }
    }
    // Outreach mode paints every cluster one neutral fill instead of
    // averaging states: at real data volume the household precedence rule
    // saturates (one opted_in voter among hundreds still paints green). See
    // clusterIconColor / OUTREACH_CLUSTER_FILL in mapColors.ts.
    const { bg, text } = clusterIconColor(viewMode, voters);
    const size = total < 100 ? 36 : total < 500 ? 44 : 52;
    return L.divIcon({
      html: `<div style="background:${bg};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:${text};font-weight:bold;font-size:${size < 40 ? 11 : 13}px;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)">${total}</div>`,
      className: 'canvass-cluster',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  // NC bounding box — drop any marker geocoded out-of-state so a single
  // bad row (e.g. "CONFIDENTIAL" geocoded to LA) doesn't yank fitBounds
  // to the middle of the country.
  const NC_LAT_MIN = 33.5, NC_LAT_MAX = 37.0;
  const NC_LNG_MIN = -84.6, NC_LNG_MAX = -75.3;
  const inNC = (lat: number, lng: number) =>
    lat >= NC_LAT_MIN && lat <= NC_LAT_MAX && lng >= NC_LNG_MIN && lng <= NC_LNG_MAX;

  async function onMarkerClick(id: string) {
    // Multi-voter at same address: if more than one VoterMapPoint shares this
    // exact lat/lng, hand the parent a picker payload instead of opening detail
    // for whichever pin happened to be on top. Geocoders return identical
    // coords for the same building, so this catches families/roommates.
    const all = currentPoints;
    const clicked = all.find(p => p.id === id);
    if (!clicked) return;
    const sameLocation = all.filter(
      p => p.lat === clicked.lat && p.lng === clicked.lng,
    );
    if (sameLocation.length > 1) {
      dispatch('selectGroup', sameLocation);
      return;
    }
    // Single voter — fetch full detail and dispatch. Track click index so a
    // rapid second click that finishes first doesn't get overwritten by a
    // stale earlier response.
    const myClick = ++lastClickAt;
    try {
      const res = await apiFetchAuthed(`/api/voters/${id}`);
      if (!res.ok) throw new Error(`GET /api/voters/${id} -> ${res.status}`);
      const full = (await res.json()) as VoterSummary;
      if (myClick !== lastClickAt || destroyed) return;
      dispatch('selectVoter', full);
    } catch (e) {
      console.error('Failed to load voter detail', e);
      toast('Could not load voter — tap again', 'error');
    }
  }

  /** Called by parent when picker user picks a single voter from the list. */
  export async function loadVoterById(id: string) {
    const myClick = ++lastClickAt;
    try {
      const res = await apiFetchAuthed(`/api/voters/${id}`);
      if (!res.ok) throw new Error(`GET /api/voters/${id} -> ${res.status}`);
      const full = (await res.json()) as VoterSummary;
      if (myClick !== lastClickAt || destroyed) return;
      dispatch('selectVoter', full);
    } catch (e) {
      console.error('Failed to load voter detail', e);
      toast('Could not load voter — tap again', 'error');
    }
  }

  function addVoterMarkers(points: VoterMapPoint[], doFitBounds = true) {
    currentPoints = points;
    clusterGroup.clearLayers();
    markerMap.clear();
    // DNC voters stay on the map (with a distinct "do not contact" marker)
    // so volunteers know not to approach the house instead of being
    // surprised by an empty pin where a registered voter lives.
    const withCoords = points.filter(
      v => v.lat !== null && v.lng !== null && inNC(v.lat!, v.lng!),
    );
    // Group voters that share an exact lat/lng — geocoders return identical
    // coords for everyone living in the same building, and stacking N markers
    // there only ever showed the topmost one. One household marker (averaged
    // support color + count badge) is both faster to render and more honest.
    const groups = new Map<string, VoterMapPoint[]>();
    for (const v of withCoords) {
      const k = `${v.lat},${v.lng}`;
      const arr = groups.get(k);
      if (arr) arr.push(v); else groups.set(k, [v]);
    }
    const bounds = L.latLngBounds([]);
    // Build all markers up front, then addLayers([...]) so Leaflet.markercluster's
    // chunkedLoading actually kicks in (it only applies to the bulk API). The
    // per-marker addLayer() path is synchronous and blocks for ~3s on 24k pins.
    const markers: L.Marker[] = [];
    for (const arr of groups.values()) {
      if (arr.length === 1) {
        const v = arr[0];
        const m = L.marker([v.lat!, v.lng!], { icon: buildIcon(v) }) as MarkerAux;
        m._voterData = v;
        m.on('click', () => onMarkerClick(v.id));
        markerMap.set(v.id, m);
        bounds.extend([v.lat!, v.lng!]);
        markers.push(m);
      } else {
        const first = arr[0];
        const m = L.marker([first.lat!, first.lng!], { icon: buildHouseholdIcon(arr) }) as MarkerAux;
        m._householdVoters = arr;
        // Dispatch the household's voter list directly — preserves the filter
        // subset the user is currently looking at, instead of re-reading the
        // (potentially stale / unfiltered) global store.
        m.on('click', () => {
          if (destroyed) return;
          dispatch('selectGroup', arr);
        });
        for (const v of arr) markerMap.set(v.id, m);
        bounds.extend([first.lat!, first.lng!]);
        markers.push(m);
      }
    }
    clusterGroup.addLayers(markers);
    applyDimToMarkers();
    // Keep the heat layer in sync with the rendered point set when active.
    updateHeatLayer();
    if (doFitBounds && bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }

  /** Called by parent after a contact is recorded or a voter edit toggles DNC. */
  export function refreshPin(updated: VoterSummary) {
    // Keep the store in step first, even when no marker is drawn for this
    // voter (filtered out), so a later rebuild from the store is not stale.
    const patch = (points: VoterMapPoint[]) =>
      points.map(p => (p.id === updated.id ? refreshedMapPoint(p, updated) : p));
    mapPointsStore.update(patch);
    currentPoints = patch(currentPoints);
    // The uncontacted heat layer is built from the store, so a knocked door
    // leaves the heat as soon as it is recorded.
    if (viewMode === 'uncontacted') updateHeatLayer();
    const m = markerMap.get(updated.id) as MarkerAux | undefined;
    if (!m) return;
    if (m._householdVoters) {
      const i = m._householdVoters.findIndex(v => v.id === updated.id);
      if (i < 0) return;
      m._householdVoters[i] = refreshedMapPoint(m._householdVoters[i], updated);
      m.setIcon(buildHouseholdIcon(m._householdVoters));
    } else {
      m._voterData = refreshedPinData(m._voterData, updated);
      m.setIcon(buildIcon(m._voterData));
    }
    // markercluster caches cluster icons; recolour the clusters holding this
    // marker instead of waiting for the next zoom.
    clusterGroup?.refreshClusters(m);
  }

  /** Called by parent to replace all visible markers (filter re-fetch).
   * The filter-change path uses full VoterSummary records from /api/voters
   * (so server-side filters can be applied). We only need contact/support
   * fields for rendering — the rest is harmless extra. */
  export function refreshMarkers(voters: VoterSummary[], fitBounds = true) {
    const points = voters.map(mapPointFromVoter);
    if (!clusterGroup) {
      // No map yet (the route only redraws after 'ready', so this is the
      // unmounted case): remember the set without drawing it.
      currentPoints = points;
      return;
    }
    addVoterMarkers(points, fitBounds);
  }

  /** Toggle live location tracking on/off. */
  function toggleLocation() {
    if (!hasGeolocation) return;
    if (locationActive) {
      stopLocation();
    } else {
      startLocation();
    }
  }

  function startLocation() {
    // First fix: drop/update the dot and pan to it ONCE, then start a watch
    // that only moves the dot (never pans, so manual panning isn't fought).
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude: lat, longitude: lng } = pos.coords;
        if (!locationDot) {
          locationDot = L.circleMarker([lat, lng], {
            radius: 8, color: '#1d6fa4', fillColor: '#4fc3f7',
            fillOpacity: 0.9, weight: 2,
          }).addTo(map);
        } else {
          locationDot.setLatLng([lat, lng]);
        }
        map.panTo([lat, lng]);
        locationActive = true;
        if (locationWatchId === null) {
          locationWatchId = navigator.geolocation.watchPosition(
            p => {
              locationDot?.setLatLng([p.coords.latitude, p.coords.longitude]);
            },
            err => {
              if (err.code === err.PERMISSION_DENIED) {
                locationDenied = true;
                stopLocation();
              }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
          );
        }
      },
      err => {
        if (err.code === err.PERMISSION_DENIED) locationDenied = true;
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  }

  function stopLocation() {
    if (locationWatchId !== null) {
      navigator.geolocation.clearWatch(locationWatchId);
      locationWatchId = null;
    }
    if (locationDot) {
      locationDot.remove();
      locationDot = null;
    }
    locationActive = false;
  }

  const CARTO_BASEMAPS_KEY = 'cb1_3jc7_1_41f3f5dd7a451ca23f72e54b';

  onMount(async () => {
    map = L.map(mapEl).setView([35.4, -79.3], 10);
    // Carto Voyager, retina via {r}, OSM-policy-safe. Carto now watermarks
    // keyless tiles with "API KEY REQUIRED". The basemaps key is a public,
    // client-side key: it travels in every tile request, so it is not a secret.
    L.tileLayer(`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAPS_KEY}`, {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    clusterGroup = (L as any).markerClusterGroup({
      maxClusterRadius: 80,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      chunkedLoading: true,
      disableClusteringAtZoom: 17,
      iconCreateFunction: buildClusterIcon,
    });
    clusterGroup.addTo(map);

    try {
      // Instant first paint from IndexedDB cache (desktop/tablet only).
      const cached = await loadCachedMapPoints();
      if (destroyed) return;
      if (cached) {
        mapPointsStore.set(cached.points);
        addVoterMarkers(cached.points, true);
      }

      // Always fetch fresh in the background. If we already painted from cache,
      // don't re-fit bounds on the repaint; preserve the user's pan/zoom.
      await fetchMapPoints();
      if (destroyed) return;
      addVoterMarkers(get(mapPointsStore), /* doFitBounds */ !cached);
    } finally {
      // Even a failed cache read or fetch leaves a map the route can filter.
      if (!destroyed) dispatch('ready');
    }
  });

  onDestroy(() => { destroyed = true; });

  onDestroy(() => {
    if (locationWatchId !== null) navigator.geolocation.clearWatch(locationWatchId);
    locationDot?.remove();
    heatLayer?.remove();
    heatLayer = null;
    map?.remove();
  });
</script>

<div bind:this={mapEl} class="map-container" aria-label="Voter canvassing map"></div>

{#if map}
  <PolygonDrawController {map} active={polygonMode === 'drawing'} on:cancel={() => dispatch('cancel')} />
{/if}
{#if polygonMode === 'drawing'}
  <p class="hint no-print" role="status">
    Click to add vertices · Double-click to close · ESC to cancel
  </p>
{/if}
{#if polygonMode === 'active' && $polygonStore}
  <div class="polygon-overlay no-print">
    <span class="count" title="Count of voters currently rendered on the map that fall inside the polygon. The walk-list count may be lower if attribute filters narrow the set further.">
      {polygonCount} voters in this area
    </span>
    <button class="btn-primary" on:click={() => dispatch('walkThisArea')}>
      Walk this area
    </button>
    <button class="btn-secondary" on:click={() => clearPolygon()}>Clear</button>
  </div>
{/if}

{#if hasGeolocation}
  <button
    class="location-btn"
    class:active={locationActive}
    on:click={toggleLocation}
    aria-pressed={locationActive}
    aria-label="Go to my location"
    title="My location"
  >
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path fill="currentColor" d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm8.94 3A9.001 9.001 0 0 0 13 3.06V1h-2v2.06A9.001 9.001 0 0 0 3.06 11H1v2h2.06A9.001 9.001 0 0 0 11 20.94V23h2v-2.06A9.001 9.001 0 0 0 20.94 13H23v-2h-2.06zM12 19a7 7 0 1 1 0-14 7 7 0 0 1 0 14z"/>
    </svg>
  </button>
{/if}

{#if locationDenied}
  <p class="location-denied" role="alert">
    Allow location access in browser settings to use this feature.
  </p>
{/if}

<style>
  .map-container {
    position: absolute;
    inset: 0;
    z-index: 0;
  }
  .location-btn {
    position: fixed;
    top: 4rem;
    right: 1rem;
    z-index: 1000;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: none;
    background: white;
    color: #1f2937;
    box-shadow: 0 2px 8px rgba(0,0,0,.25);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .location-btn:hover {
    color: #1d4ed8;
  }
  .location-btn.active {
    background: #1d4ed8;
    color: white;
  }
  .location-btn:focus-visible {
    outline: 3px solid #4fc3f7;
    outline-offset: 2px;
  }
  .location-denied {
    position: fixed;
    top: 7.5rem;
    right: 1rem;
    z-index: 1000;
    background: white;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    font-size: 0.8rem;
    max-width: 220px;
    box-shadow: 0 2px 6px rgba(0,0,0,.2);
  }
  :global(.leaflet-marker-icon.dimmed) { opacity: 0.25; transition: opacity 0.15s; }
  .hint {
    position: absolute;
    top: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    background: var(--ink, #0b0b0b);
    color: var(--paper, #f7f3ea);
    padding: 0.4rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.85rem;
    z-index: 1000;
    pointer-events: none;
  }
  .polygon-overlay {
    position: absolute;
    top: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    background: var(--paper, #f7f3ea);
    color: var(--ink, #0b0b0b);
    border: 1px solid var(--leaf-bright, #4a8a3e);
    padding: 0.4rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    z-index: 1000;
  }
  .polygon-overlay .count { font-weight: 600; }
  .polygon-overlay .btn-primary {
    background: var(--leaf-bright, #4a8a3e);
    color: var(--paper, #f7f3ea);
    border: none;
    padding: 0.25rem 0.6rem;
    border-radius: 0.2rem;
    cursor: pointer;
  }
  .polygon-overlay .btn-secondary {
    background: transparent;
    color: var(--ink, #0b0b0b);
    border: 1px solid var(--ink-soft, #777);
    padding: 0.25rem 0.6rem;
    border-radius: 0.2rem;
    cursor: pointer;
  }
</style>
